<template>
  <div class="dashboard-mural">
    <div class="mural-header">
      <div class="header-content">
        <h1 class="greeting">Olá, {{ userName }}! 👋</h1>
        <p class="subtitle">Bem-vindo(a) ao seu mural de atualizações e prioridades do dia.</p>
      </div>
      <div class="header-date">
        <feather-icon icon="CalendarIcon" size="18" />
        <span>{{ currentDate }}</span>
      </div>
    </div>

    <!-- Comunicados Section -->
    <div class="section-title mt-2">
      <feather-icon icon="RadioIcon" size="20" class="mr-50 text-primary" />
      <h2>Mural de Comunicados</h2>
    </div>
    
    <b-overlay :show="loadingComunicados" variant="light" blur="2px" rounded="sm" class="comunicados-overlay">
      <div class="comunicados-grid" v-if="comunicados.length">
        <div 
          v-for="comunicado in comunicados" 
          :key="comunicado.comunicadoid"
          class="comunicado-card glass-panel"
        >
          <div class="comunicado-meta">
            <span class="comunicado-date">{{ formatDate(comunicado.comunicadodatainicial) }}</span>
            <b-badge :variant="getComunicadoBadge(comunicado.comunicadotipotexto)">
              {{ comunicado.comunicadotipotexto || 'Aviso' }}
            </b-badge>
          </div>
          <h3 class="comunicado-title">{{ comunicado.comunicadotitulo_ptbr || 'Comunicado' }}</h3>
          <p class="comunicado-body">{{ comunicado.comunicadoconteudo_ptbr }}</p>
        </div>
      </div>
      <div v-else-if="!loadingComunicados" class="glass-panel empty-state">
        <feather-icon icon="InboxIcon" size="40" class="text-muted mb-1" />
        <p>Nenhum comunicado ativo no momento.</p>
      </div>
    </b-overlay>

    <div class="stats-grid mt-3" :class="{ 'stats-grid-representante': ehRepresentante }">
      <!-- Andamento dos pedidos de doação (só Representante): resumo de
           rascunhos, pedidos em aprovação e situação geral. -->
      <div v-if="ehRepresentante" class="mural-widget glass-panel">
        <div class="widget-header">
          <feather-icon icon="GiftIcon" size="20" class="text-primary" />
          <h3>Pedidos de Doação</h3>
        </div>
        <b-overlay :show="loadingDoacao" variant="light" blur="2px">
          <div class="widget-body">
            <div class="stat-row">
              <span class="stat-label">Rascunhos (pré-pedidos):</span>
              <span class="stat-value text-info">{{ doacaoResumo.rascunhos }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">Aguardando aprovação:</span>
              <span class="stat-value text-warning">{{ doacaoResumo.emAprovacao }}</span>
            </div>
            <div
              v-for="s in doacaoResumo.porStatus"
              :key="s.status"
              class="stat-row"
            >
              <span class="stat-label">{{ s.status }}:</span>
              <span class="stat-value text-primary">{{ s.total }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">Total de pedidos:</span>
              <span class="stat-value text-success">{{ doacaoResumo.total }}</span>
            </div>
            <div class="widget-action mt-2">
              <b-button variant="outline-primary" size="sm" block @click="$router.push({ name: 'pedidos-vendas' })">
                Ver Pedidos
              </b-button>
            </div>
          </div>
        </b-overlay>
      </div>

      <!-- Vendas Resumo -->
      <div class="mural-widget glass-panel">
        <div class="widget-header">
          <feather-icon icon="DollarSignIcon" size="20" class="text-success" />
          <h3>Desempenho (Mês Atual)</h3>
        </div>
        <b-overlay :show="loadingVendas" variant="light" blur="2px">
          <div class="widget-body" v-if="vendasResumo">
            <div class="stat-row">
              <span class="stat-label">{{ ehDoacao ? 'Valor Doado:' : 'Valor Vendido:' }}</span>
              <span class="stat-value text-success">{{ formatCurrency(vendasResumo.valorTotalPedidos) }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">Pedidos Realizados:</span>
              <span class="stat-value text-primary">{{ vendasResumo.totalPedidos || 0 }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">Clientes Atendidos:</span>
              <span class="stat-value text-info">{{ vendasResumo.totalClientes || 0 }}</span>
            </div>
            <div class="widget-action mt-2">
              <b-button variant="outline-primary" size="sm" block @click="$router.push({ name: 'dashboard-equipe-vendas' })">
                {{ ehDoacao ? 'Ver Dashboard de Doação' : 'Ver Dashboard de Vendas' }}
              </b-button>
            </div>
          </div>
        </b-overlay>
      </div>

      <!-- Agenda de Atividades -->
      <div v-if="!ehRepresentante" class="mural-widget glass-panel">
        <div class="widget-header">
          <feather-icon icon="ClockIcon" size="20" class="text-warning" />
          <h3>Minhas Atividades (Agenda)</h3>
        </div>
        <b-overlay :show="loadingAgenda" variant="light" blur="2px">
          <div class="widget-body activities-list" v-if="atividades.length">
            <div 
              v-for="atividade in atividades" 
              :key="atividade.id"
              class="activity-item"
            >
              <div class="activity-icon">
                <feather-icon :icon="getActivityIcon(atividade.tipo_atividade)" size="14" />
              </div>
              <div class="activity-details">
                <span class="act-title">{{ atividade.assunto }}</span>
                <span class="act-client">{{ atividade.cliente_fantasia || atividade.cliente_razao || 'Sem Cliente' }}</span>
              </div>
              <div class="activity-time" :class="{'is-overdue': isOverdue(atividade.data_vencimento)}">
                {{ formatTime(atividade.data_vencimento) }}
              </div>
            </div>
            <div class="widget-action mt-2">
              <b-button variant="outline-warning" size="sm" block @click="$router.push({ name: 'crm-dashboard' })">
                Abrir CRM
              </b-button>
            </div>
          </div>
          <div v-else-if="!loadingAgenda" class="empty-state-sm">
            <p>Nenhuma atividade pendente encontrada.</p>
          </div>
        </b-overlay>
      </div>

      <!-- Aniversariantes -->
      <div v-if="!ehRepresentante" class="mural-widget glass-panel">
        <div class="widget-header">
          <feather-icon icon="GiftIcon" size="20" class="text-danger" />
          <h3>Aniversariantes do Mês</h3>
        </div>
        <b-overlay :show="loadingAniversarios" variant="light" blur="2px">
          <div class="widget-body" v-if="aniversariantes.length">
             <div 
              v-for="(item, idx) in aniversariantes.slice(0, 4)" 
              :key="idx"
              class="birthday-item"
            >
              <div class="birthday-avatar">
                {{ getInitials(item.nome) }}
              </div>
              <div class="birthday-info">
                <span class="birthday-name">{{ item.nome }}</span>
                <span class="birthday-day">Dia {{ item.dia }} • {{ item.cliente_fantasia || item.cliente_razao }}</span>
              </div>
            </div>
            <div class="widget-action mt-2">
              <b-button variant="outline-danger" size="sm" block @click="$root.$emit('open-birthdays-modal')">
                Ver Todos ({{ aniversariantes.length }})
              </b-button>
            </div>
          </div>
          <div v-else-if="!loadingAniversarios" class="empty-state-sm">
            <p>Nenhum aniversariante encontrado neste mês.</p>
          </div>
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import { BOverlay, BBadge, BButton } from 'bootstrap-vue';
import axios from '@/libs/axios';
import crmClient from '@/api/CRM/crm.client';

export default {
  name: 'DashboardMural',
  components: {
    BOverlay,
    BBadge,
    BButton,
  },
  data() {
    return {
      userName: '',
      loadingComunicados: false,
      loadingDoacao: false,
      doacaoResumo: {
        rascunhos: 0,
        emAprovacao: 0,
        porStatus: [],
        total: 0,
      },
      loadingVendas: false,
      loadingAgenda: false,
      loadingAniversarios: false,
      comunicados: [],
      vendasResumo: null,
      atividades: [],
      aniversariantes: [],
    };
  },
  computed: {
    ehDoacao() {
      try {
        const perfil = JSON.parse(localStorage.getItem('perfilLogado') || '{}');
        return ['representante', 'supervisor'].includes(String(perfil.perfil_acesso_nome || '').trim().toLowerCase());
      } catch (_) { return false; }
    },
    ehRepresentante() {
      try {
        const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado') || '{}');
        return (perfilLogado.perfil_acesso_nome || '').trim() === 'Representante';
      } catch (e) {
        return false;
      }
    },
    currentDate() {
      return new Date().toLocaleDateString('pt-BR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
  },
  created() {
    this.extractUserName();
    this.fetchAllData();
  },
  methods: {
    extractUserName() {
      try {
        const userData = JSON.parse(localStorage.getItem('userData'));
        if (userData && userData.usuarionome) {
          this.userName = userData.usuarionome.split(' ')[0];
        } else {
          this.userName = 'Representante';
        }
      } catch (e) {
        this.userName = 'Representante';
      }
    },
    async fetchAllData() {
      this.fetchComunicados();
      this.fetchVendas();
      if (this.ehRepresentante) {
        this.fetchDoacaoResumo();
      } else {
        this.fetchAgenda();
        this.fetchAniversariantes();
      }
    },
    // Resumo do andamento dos pedidos de doação do representante:
    // contagem por situação (mesma fonte dos KPIs da listagem) + rascunhos.
    async fetchDoacaoResumo() {
      this.loadingDoacao = true;
      try {
        const repId = this.getRepresentanteId();
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');

        const [statusResp, rascResp] = await Promise.allSettled([
          axios.get('/dashboard/equipe-vendas', { params: { representanteid: repId } }),
          repId && userData.usuarioid
            ? axios.get(`/pedido-venda/listarRascunhos/Representante/${repId}/${userData.usuarioid}`, { params: { page: 1, pageSize: 1, tipopedidoid: 'WD' } })
            : Promise.resolve(null),
        ]);

        let porStatus = [];
        if (statusResp.status === 'fulfilled') {
          const raw = statusResp.value?.data;
          const lista = raw?.pedidosPorStatus || [];
          porStatus = lista
            .map((s) => ({
              status: s.pedidositcomercial || s.pedidoSitComercial || s.status || '—',
              total: Number(s.Total ?? s.total ?? 0),
            }))
            .filter((s) => s.total > 0);
        }
        const emAprovacaoRow = porStatus.find((s) => String(s.status).toUpperCase().includes('APROVA') && !String(s.status).toUpperCase().startsWith('APROVADO'));
        const total = porStatus.reduce((acc, s) => acc + s.total, 0);

        let rascunhos = 0;
        if (rascResp.status === 'fulfilled' && rascResp.value) {
          const meta = rascResp.value?.data?.meta || rascResp.value?.data;
          rascunhos = Number(meta?.total || 0);
        }

        this.doacaoResumo = {
          rascunhos,
          emAprovacao: emAprovacaoRow ? emAprovacaoRow.total : 0,
          porStatus: porStatus.filter((s) => s !== emAprovacaoRow),
          total,
        };
      } catch (error) {
        console.error('Erro ao buscar resumo de doações:', error);
      } finally {
        this.loadingDoacao = false;
      }
    },
    async fetchComunicados() {
      this.loadingComunicados = true;
      try {
        const { data } = await axios.get('/comunicado', {
          params: {
            ativosVigentes: true,
            page: 1,
            perPage: 4,
            sortBy: 'comunicadodatainicial',
            sortDesc: true,
          },
        });
        const fetched = Array.isArray(data)
          ? data
          : (data.data || data.content || []);
        this.comunicados = fetched.slice(0, 4);

      } catch (error) {
        console.error('Erro ao buscar comunicados:', error);
      } finally {
        this.loadingComunicados = false;
      }
    },
    async fetchVendas() {
      this.loadingVendas = true;
      try {
        const repId = this.getRepresentanteId();
        
        const date = new Date();
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0];

        const { data } = await axios.get('/dashboard/equipe-vendas', {
          params: { representanteid: repId, dataInicio: startOfMonth, dataFim: endOfMonth },
        });
        if (data && data.resumo) {
          this.vendasResumo = data.resumo;
        }
      } catch (error) {
        console.error('Erro ao buscar vendas:', error);
      } finally {
        this.loadingVendas = false;
      }
    },
    async fetchAgenda() {
      this.loadingAgenda = true;
      try {
        // Fetch ALL pending activities. The backend already sorts by data_inicio ASC.
        // We limit to top 5 to show the 5 most imminent activities.
        const { data } = await crmClient.listAtividades({ 
          status: 'Pendente', 
          limit: 5 
        });
        console.log("Agenda returned data: ", data);
        this.atividades = data.atividades || data.items || data || [];
        this.atividades = this.atividades.slice(0, 5); // Limit to top 5
      } catch (error) {
        console.error('Erro ao buscar agenda:', error);
      } finally {
        this.loadingAgenda = false;
      }
    },
    async fetchAniversariantes() {
      this.loadingAniversarios = true;
      try {
        const currentMonth = new Date().getMonth() + 1;
        const response = await crmClient.aniversariosMes(currentMonth);
        this.aniversariantes = response.data || [];
      } catch (error) {
        console.error('Erro ao buscar aniversários:', error);
      } finally {
        this.loadingAniversarios = false;
      }
    },
    getRepresentanteId() {
      try {
        const representanteData = JSON.parse(localStorage.getItem('representanteData'));
        if (representanteData?.representanteid) return representanteData.representanteid;
        const userData = JSON.parse(localStorage.getItem('userData'));
        if (userData?.representanteid) return userData.representanteid;
        return '';
      } catch { return ''; }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('pt-BR');
    },
    formatTime(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },
    formatCurrency(val) {
      return 'R$ ' + Number(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    getComunicadoBadge(tipo) {
      const typeStr = (tipo || '').toLowerCase();
      if (typeStr.includes('urgente') || typeStr.includes('importante')) return 'light-danger';
      if (typeStr.includes('promo')) return 'light-success';
      return 'light-primary';
    },
    getActivityIcon(type) {
      const t = (type || '').toLowerCase();
      if (t.includes('lig')) return 'PhoneIcon';
      if (t.includes('email')) return 'MailIcon';
      if (t.includes('visit')) return 'MapPinIcon';
      return 'CheckSquareIcon';
    },
    isOverdue(dateStr) {
      if (!dateStr) return false;
      return new Date(dateStr) < new Date();
    },
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
    },
  },
};
</script>

<style scoped>
.dashboard-mural {
  padding: 1.5rem;
}

/* Glassmorphism Classes */
.glass-panel {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(143, 222, 234, 0.4);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04);
  padding: 1.5rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.glass-panel:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(1, 9, 78, 0.08);
  border-color: rgba(0, 183, 214, 0.4);
}

body.dark-layout .glass-panel {
  background: rgba(1, 9, 78, 0.6);
  border-color: rgba(143, 222, 234, 0.15);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

body.dark-layout .glass-panel:hover {
  background: rgba(1, 9, 78, 0.8);
  border-color: rgba(0, 183, 214, 0.3);
}

/* Header */
.mural-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.mural-header .greeting {
  font-size: 2rem;
  font-weight: 800;
  color: #01094e;
  margin-bottom: 0.25rem;
  letter-spacing: -0.02em;
}

.mural-header .subtitle {
  color: #0575b5;
  font-size: 1.05rem;
  opacity: 0.85;
}

.header-date {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #00b7d6;
  background: rgba(0, 183, 214, 0.1);
  padding: 0.75rem 1.25rem;
  border-radius: 50px;
}

body.dark-layout .mural-header .greeting { color: #d6f4fa; }
body.dark-layout .mural-header .subtitle { color: #8fdeea; }

/* Sections */
.section-title {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}
.section-title h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #01094e;
  margin-bottom: 0;
}
body.dark-layout .section-title h2 { color: #d6f4fa; }

/* Comunicados Grid */
.comunicados-overlay {
  min-height: 120px;
}
.comunicados-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.25rem;
}
.comunicado-card {
  display: flex;
  flex-direction: column;
}
.comunicado-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}
.comunicado-date {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 600;
}
.comunicado-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #01094e;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}
.comunicado-body {
  font-size: 0.95rem;
  color: #4b5563;
  margin-bottom: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

body.dark-layout .comunicado-date { color: #8fdeea; }
body.dark-layout .comunicado-title { color: #d6f4fa; }
body.dark-layout .comunicado-body { color: #cbd5e1; }

/* Stats & Extra Widgets Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
  gap: 1.25rem;
}

.stats-grid-representante {
  width: 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stats-grid-representante > .mural-widget {
  min-width: 0;
}

@media (max-width: 991.98px) {
  .stats-grid-representante {
    grid-template-columns: minmax(0, 1fr);
  }
}

.widget-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  border-bottom: 1px solid rgba(143, 222, 234, 0.2);
  padding-bottom: 0.75rem;
}

.widget-header h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #01094e;
  margin: 0;
}
body.dark-layout .widget-header h3 { color: #d6f4fa; }

/* Dashboard Resumo */
.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px dashed rgba(143, 222, 234, 0.2);
}
.stat-row:last-child {
  border-bottom: none;
}
.stat-label {
  font-size: 0.95rem;
  color: #4b5563;
  font-weight: 500;
}
.stat-value {
  font-size: 1.1rem;
  font-weight: 700;
}
body.dark-layout .stat-label { color: #cbd5e1; }

/* Agenda Widget */
.activities-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.activity-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem;
  background: rgba(5, 117, 181, 0.05);
  border-radius: 8px;
  transition: background 0.2s;
}
.activity-item:hover {
  background: rgba(0, 183, 214, 0.1);
}
.activity-icon {
  width: 32px;
  height: 32px;
  background: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0575b5;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
  flex-shrink: 0;
}
.activity-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.act-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #01094e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.act-client {
  font-size: 0.8rem;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.activity-time {
  font-size: 0.8rem;
  font-weight: 600;
  color: #0575b5;
  text-align: right;
}
.activity-time.is-overdue {
  color: #dc2626;
}

body.dark-layout .activity-item { background: rgba(0, 0, 0, 0.2); }
body.dark-layout .activity-item:hover { background: rgba(0, 183, 214, 0.15); }
body.dark-layout .activity-icon { background: #1e293b; color: #8fdeea; }
body.dark-layout .act-title { color: #d6f4fa; }
body.dark-layout .act-client { color: #94a3b8; }

/* Birthdays */
.birthday-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.birthday-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #01094e, #0575b5);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
  flex-shrink: 0;
}
.birthday-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.birthday-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #01094e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.birthday-day {
  font-size: 0.8rem;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

body.dark-layout .birthday-name { color: #d6f4fa; }
body.dark-layout .birthday-day { color: #94a3b8; }

/* Empty States */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: #6c757d;
}
.empty-state-sm {
  padding: 1.5rem 1rem;
  text-align: center;
  color: #6c757d;
  font-size: 0.95rem;
  font-style: italic;
}
body.dark-layout .empty-state, body.dark-layout .empty-state-sm { color: #8fdeea; }
</style>
